import { getCookie } from "./hubspotUtils";

const AD_CLICK_STORAGE_KEY = "adClickIds";

/**
 * The identifiers Google and Meta match a conversion against. The FirstFunded
 * conversion is recorded server-side, so the customer's browser is not present when
 * it happens — these are captured here while it is, and persisted against the
 * account so the backend can attribute the conversion later.
 */

// GA4 writes _ga as "GA1.<domainDepth>.<clientId>", where clientId is itself
// "<random>.<firstVisitSeconds>". Measurement Protocol wants the clientId alone.
export const getGaClientId = () => {
  const raw = getCookie("_ga");
  if (!raw) return "";
  const parts = raw.split(".");
  if (parts.length < 4) return "";
  return parts.slice(2).join(".");
};

// _fbp identifies the browser to Meta and is set on every visit.
export const getMetaBrowserId = () => getCookie("_fbp") || "";

export const getStoredAdClickIds = () => {
  try {
    return JSON.parse(localStorage.getItem(AD_CLICK_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

// A stored entry can carry a single shared `capturedAt` rather than a time per click
// id. That is then the only time available for either id, so it stands in until the
// id is captured again and gets one of its own.
const getCaptureTime = (stored, idKey) =>
  stored[`${idKey}CapturedAt`] || stored.capturedAt || "";

// gclid and fbclid arrive as query params on the ad landing page, but signup happens
// several navigations later, so they have to outlive the URL that carried them. A
// newer click id replaces an older one; a visit without one leaves the stored value
// alone. Each id keeps the time of its own click, because a shared timestamp would be
// moved by a click on the other platform onto an id that click never touched.
export const storeAdClickIds = () => {
  const params = new URLSearchParams(window.location.search);
  const gclid = params.get("gclid") || "";
  const fbclid = params.get("fbclid") || "";
  if (!gclid && !fbclid) return;

  const stored = getStoredAdClickIds();
  const capturedAt = new Date().toISOString();
  try {
    localStorage.setItem(
      AD_CLICK_STORAGE_KEY,
      JSON.stringify({
        gclid: gclid || stored.gclid || "",
        fbclid: fbclid || stored.fbclid || "",
        gclidCapturedAt: gclid ? capturedAt : getCaptureTime(stored, "gclid"),
        fbclidCapturedAt: fbclid ? capturedAt : getCaptureTime(stored, "fbclid")
      })
    );
  } catch {
    // Storage can be disabled, full, or blocked outright — Safari's private mode throws
    // a SecurityError on write. This runs before the app renders, and only for a visitor
    // who arrived on an ad, so letting it throw would blank the page for exactly the
    // traffic we paid for. Losing the click id is the smaller loss.
  }
};

// _fbc encodes the Meta ad click. The Pixel sets it when a visitor arrives with an
// fbclid; when the Pixel is blocked or the cookie has expired, Meta accepts the same
// value rebuilt from the stored fbclid in its documented
// "fb.<subdomainIndex>.<creationMs>.<fbclid>" form.
export const getMetaClickId = () => {
  const cookie = getCookie("_fbc");
  if (cookie) return cookie;

  const stored = getStoredAdClickIds();
  if (!stored.fbclid) return "";

  // Meta matches on when the ad was clicked, so this is the fbclid's own capture time.
  const creationMs = Date.parse(getCaptureTime(stored, "fbclid")) || Date.now();
  return `fb.1.${creationMs}.${stored.fbclid}`;
};

/**
 * Forgets the stored ad click once an account has taken ownership of it.
 *
 * A click belongs to one customer. Two people sharing a browser — a shared work machine,
 * a family computer — would otherwise both send the same gclid and fbclid, and because
 * the backend writes a click id once and keeps it, the second customer's conversion
 * would be credited to the first customer's ad for good.
 *
 * The browser identifiers are not stored here: they live in the _ga and _fbp cookies, so
 * clearing this leaves the next capture able to refresh them.
 */
export const clearStoredAdClickIds = () => {
  try {
    localStorage.removeItem(AD_CLICK_STORAGE_KEY);
  } catch {
    // Same unavailable-storage cases as the write. Nothing to clean up if it throws.
  }
};

// Empty identifiers are dropped rather than sent as "", so a later capture that does
// find a value is not indistinguishable from one that found nothing.
export const buildMarketingIdentifiers = () => {
  const identifiers = {
    gaClientId: getGaClientId(),
    metaBrowserId: getMetaBrowserId(),
    metaClickId: getMetaClickId(),
    gclid: getStoredAdClickIds().gclid || ""
  };

  return Object.fromEntries(
    Object.entries(identifiers).filter(([, value]) => value)
  );
};
